import { levelLabelFromWordCount, localizedLevelLabel } from './constants'
import { LocalizedText, storyTitleText } from '../../i18n/localizedContent'

const TARGET_KNOWN_WORDS_PERCENTAGE = 98

/** Story tag for badge display */
export type StoryTag = 'recommended' | 'featured' | 'latest'

/** Normalized display shape used by all StoryCard variants */
export interface ParsedStoryForCard {
  /** Raw Chinese title (component applies character conversion) */
  title: string
  englishTitle: string
  /** The title to show under the Chinese: `localizedTitle` when the API sent one, else English. */
  translatedTitle: LocalizedText
  imageUrl: string | null
  knownWords: number | null
  newWords: number | null
  diff: number
  isRead: boolean
  tags: StoryTag[]
  levelLabel: string | null
}

/**
 * Stories from getStories have: storyTitle, englishTitle, image (string), knownWords, newWords, diff, is_read, is_recommended, is_latest.
 * Stories from getStoriesByLevel have: title[0].text, english_title[0].text, image?.url, knownWords, newWords, diff, is_read, unique_word_count, level.
 */
export function parseStoryForCard(story: any): ParsedStoryForCard {
  if (!story) {
    return {
      title: '',
      englishTitle: '',
      translatedTitle: { text: '', lang: 'en', localized: false },
      imageUrl: null,
      knownWords: null,
      newWords: null,
      diff: 0,
      isRead: false,
      tags: [],
      levelLabel: null,
    }
  }

  const title =
    story.storyTitle ??
    story.title?.[0]?.text ??
    story.title?.[0]?.spans?.[0]?.text ??
    ''
  const englishTitle =
    story.englishTitle ??
    story.english_title?.[0]?.text ??
    ''
  const imageUrl =
    typeof story.image === 'string'
      ? story.image
      : story.image?.url ?? null
  const knownWords =
    story.knownWords !== undefined && story.knownWords !== null
      ? Number(story.knownWords)
      : null
  const newWords =
    story.newWords !== undefined && story.newWords !== null
      ? Number(story.newWords)
      : null
  const hasDiffValue =
    story?.diff !== undefined &&
    story?.diff !== null &&
    !Number.isNaN(Number(story?.diff))
  const derivedDiffFromKnownWords =
    knownWords !== null ? Math.abs(TARGET_KNOWN_WORDS_PERCENTAGE - knownWords) : null
  // Prefer a normalized diff from knownWords so all card sources map to consistent tower bars.
  const diff = derivedDiffFromKnownWords ?? (hasDiffValue ? Number(story?.diff) : 0)
  const isRead = !!story?.is_read

  const tags: StoryTag[] = []
  if (story?.is_recommended) tags.push('recommended')
  if (story?.is_latest) tags.push('latest')
  // 'featured' is applied by parent via displayTags when rendering the single "today's story" card

  // Prefer the backend's stored combined level (story_level-aware), then fall
  // back to deriving from unique_word_count for payloads without a level.
  let levelLabel: string | null =
    localizedLevelLabel(story?.story_level ?? story?.level, story?.level_display)
  if (!levelLabel) {
    const uniqueWordCount = Number(story?.unique_word_count ?? story?.uniqueWordCount)
    levelLabel = levelLabelFromWordCount(uniqueWordCount)
  }

  return {
    title,
    englishTitle,
    translatedTitle: storyTitleText(story),
    imageUrl,
    knownWords,
    newWords,
    diff,
    isRead,
    tags,
    levelLabel,
  }
}

